import React from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import "./Breadcrumbs.css";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbDivider,
  BreadcrumbButton,
} from "@fluentui/react-breadcrumb-preview";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  displayRecordCount,
  getItemDisplayValue,
  ItemDisplayKeys,
} from "../../utils";
import { LIABILITY_CASE_TYPE_MAP } from "../../pages/client/case/Liability/LiabilityHomePage";
import { POLICY_CASE_TYPE_MAP } from "../../pages/client/case/Policy/PolicyHomePage";
import { ASSET_CASE_TYPE_MAP } from "../../pages/client/case/Asset/assetSubTypeConfig";

const breadcrumbNameMap: any = {
  clients: "Clients",
  "action-list": "Action List",
  utilities: "Utilities",
  report: "Reports",
  "system-manager": "System Manager",
  commission: "Commission",
  distribution: "Distribution",
  "other-fees-charges": "Other Fees/Charges",
  receipting: "Receipting",
  "file-upload": "Payment File Upload",
  "manage-user": "Manage User",
  "master-settings": "Master Settings",
  "commission-type": "Commission Type",
  "commission-rule": "Commission Rule",
  subscription: "Subscription",
  "company-detail": "Company Details",
  "income-category": "Income Categories",
  "expense-category": "Expense Categories",
  "attitude-risk-categories": "Attitude-to-Risk Categories",
  "attitude-risk-ratings": "Attitude-to-Risk Ratings",
  objectives: "Objectives",
  providers: "Providers",
  "user-defined": "User Defined",
  "case-action": "Case Action",
  "client-action": "Client Action",
  "email-template": "Email ",
  "letter-template": "Letter ",
  "contract-enquiry": "Contract Enquiry",
  integrations: "Integrations",
  "company-details": "Company Details",
  // Company Details' sub-pages became routes of their own when the in-page
  // tab strip moved into the sidebar.
  "basic-details": "Basic Details",
  "communication-credentials": "Communication Credentials",
  "frn-details": "FRN Details",
  customisations: "Customisations",
  templates: "Templates",
  template: "Templates",
  assets: "Assets",
  asset: "Asset",
  investment: "Investment",
  shares: "Share Holdings",
  "home-personal": "Home & Personal",
  "bank-societies": "Bank / Building Societies",
  morgages: "Mortgages",
  "loans-lease-hp": "Loan / Hire Purchase",
  "credit-cards": "Credit Cards",
  "life-assurance": "Life Assurance",
  pensions: "Pensions",
  investments: "Investments",
  "savings-plans": "Savings Plans",
  "income-protection": "Income Protection",
  "health-assurance": "Health Assurance",
  general: "General",
  view: "View",
  edit: "Edit",
  add: "Add",
  review: "Review",
  "name-and-address": "Name And Address",
  contact: "Contact",
  nationality: "Nationality",
  personal: "Personal",
  "health-note": "Health Note",
  vulnerability: "Vulnerability",
  employment: "Employment",
  "attitude-to-risk": "Attitude to Risk",
  identity: "Identity",
  administration: "Administration",
  compliance: "Compliance",
  "service-type": "Service Type",
  "client-summary": "Client Summary",
  "case-summary": "Case Summary",
  "contact-history": "Contact History",
  partner: "Partner",
  payments: "Payments",
  payment: "Payment",
  withdrawals: "Withdrawals",
  actions: "Actions",
  valuations: "Valuations",
  funds: "Funds",
  fund: "Fund",
  withdrawal: "Withdrawal",
  action: "Action",
  valuation: "Valuation",
  liabilities: "Liabilities",
  liability: "Liability",
  policies: "Policies",
  policy: "Policy",
  dependant: "Dependant",
  income: "Income",
  outgoings: "Outgoings",
  "address-book": "Address Book",
  "fact-find-notes": "Fact Find Notes",
  "fees-charges": "Fees/Charges",
  notes: "Notes",
  "time-allocation": "Time Allocation",
  documents: "Documents",
  "assyst-drive": "Assyst Drive",
  "file-link": "File Link",
  contacts: "Contacts",
  email: "Email",
  letter: "Letter",
  cashcalc: "CashCalc",
  assyst_portal: "Assyst Portal",
  client_portal: "Client Portal",
  profile: "Profile",
  account: "Account",
  emailSettings: "Email Settings",
  run_logs: "Run Logs",
  reports: "Reports",
  "cashcalc-credentials": "CashCalc Accounts",
  "bulk-valuation-upload": "Manual BV",
  "bulk-valuation": "Bulk Valuation",
};

const Breadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    activeClient,
    assets,
    liabilities,
    policies,
    vulnerability,
    partnerVulnerability,
    clientContacts,
    incomeCategories,
    expenseCategories,
    attitudeToRiskRatings,
    attitudeToRiskCategories,
    objectives,
    caseActions,
    clientActions,
    userDefinedFields,
    providers,
    commissionRules,
    bulkValuation,
    commissionTypes,
  } = useSelector((state: RootState) => state);

  const vulnerabilityList = vulnerability?.listItems || [];
  const partnerVulnerabilityList = partnerVulnerability?.listItems || [];
  const incomeCategorylistItems = incomeCategories?.listItems;
  const expenseCategorylistItems = expenseCategories?.listItems;
  const attitudeToRiskRatingsListItems = attitudeToRiskRatings?.listItems;
  const attitudeToRiskCategoriesListItems = attitudeToRiskCategories?.listItems;
  const objectivesListItems = objectives?.listItems;
  const caseactionsListItems = caseActions?.listItems;
  const clientActionsListItems = clientActions?.listItems;
  const userDefinedListItems = userDefinedFields?.listItems;
  const providersListItems = providers?.list;
  const commisssionRulesListItems = commissionRules?.list;
  const runLogvaluationHistoryListItems = bulkValuation?.valuationHistory;
  const commissionTypesListItems = commissionTypes?.listItems;
  const userListItems = useSelector(
    (state: RootState) => state.manageUser.listItems,
  );
  const pathnames = location.pathname.split("/").filter((x) => x);
  const {
    clientId,
    case_type,
    caseId,
    subItemId,
    subSubItemId,
    action,
    selectedItemId,
    distributionId,
    itemId,
    userId,
  } = useParams();

  const handleNavigationLink = (link: any) => {
    navigate(link);
  };
  const currentPath = location.pathname.replace(/^\/+/, "");
  const hiddenPaths = ["/clients", "/", "/action-list", "/report"];

  if (hiddenPaths.includes(location.pathname)) {
    return <div></div>;
  }
  return (
    <Breadcrumb
      aria-label="Breadcrumb"
      style={{
        padding: currentPath.startsWith("profile") ? "0 20px" : 0,
      }}
    >
      <BreadcrumbItem>
        <BreadcrumbButton onClick={() => handleNavigationLink("/")}>
          Home
        </BreadcrumbButton>
      </BreadcrumbItem>
      {currentPath == "" && (
        <>
          <BreadcrumbDivider />
          <BreadcrumbItem>
            <BreadcrumbButton onClick={() => handleNavigationLink("/")} current>
              Clients
            </BreadcrumbButton>
          </BreadcrumbItem>
        </>
      )}

      {pathnames.map((value, index) => {
        const to = `/${pathnames.slice(0, index + 1).join("/")}`;
        const isLast = index === pathnames.length - 1;
        let displayValue = value;
        const pathSections = location.pathname.split("/");
        const caseType = ["assets", "liabilities", "policies"].find((type) =>
          pathSections.includes(type),
        );

        // ======= ONLY ADDED system-manager/customisations routes handling =======

        const recordMaps: any = {
          "income-category": incomeCategorylistItems,
          "expense-category": expenseCategorylistItems,
          "attitude-risk-categories": attitudeToRiskCategoriesListItems,
          "attitude-risk-ratings": attitudeToRiskRatingsListItems,
          objectives: objectivesListItems,
          "case-action": caseactionsListItems,
          "client-action": clientActionsListItems,
          "user-defined": userDefinedListItems,
          providers: providersListItems,
          "commission-rule": commisssionRulesListItems,
          "run-logs": runLogvaluationHistoryListItems,
          "commission-type": commissionTypesListItems,
        };

        // Find matching section
        const matchedSection = Object.keys(recordMaps).find((key) =>
          pathSections.includes(key),
        );
        if (value === clientId) {
          const firstName = activeClient?.basicDetails?.first_names;
          const lastName = activeClient?.basicDetails?.last_name;

          if (lastName) {
            displayValue = firstName ? `${lastName}, ${firstName}` : lastName;
          } else {
            displayValue = "";
          }
        } else if (value === caseId) {
          const idKey =
            caseType === "policies"
              ? "policy_id"
              : caseType === "liabilities"
                ? "liability_id"
                : "asset_id";

          const assetDbCaseType = case_type
            ? ASSET_CASE_TYPE_MAP[case_type]
            : undefined;
          const liabilityDbCaseType = case_type
            ? LIABILITY_CASE_TYPE_MAP[case_type]
            : undefined;
          const policyDbCaseType = case_type
            ? POLICY_CASE_TYPE_MAP[case_type]
            : undefined;
          const itemsArray =
            caseType === "policies"
              ? policyDbCaseType
                ? (policies?.listItems || []).filter(
                  (item: any) => item.case_type === policyDbCaseType,
                )
                : policies?.listItems
              : caseType === "liabilities"
                ? liabilityDbCaseType
                  ? (liabilities?.listItems || []).filter(
                    (item: any) => item.case_type === liabilityDbCaseType,
                  )
                  : liabilities?.listItems
                : assetDbCaseType
                  ? (assets?.listItems || []).filter(
                    (item: any) => item.case_type === assetDbCaseType,
                  )
                  : assets?.listItems;
          const activeItem =
            caseType === "policies"
              ? policies?.activeItem
              : caseType === "liabilities"
                ? liabilities?.activeItem
                : assets?.activeItem;
          const activeItemIndex = itemsArray?.findIndex(
            (item: any) => item[idKey] === activeItem?.[idKey],
          );

          if (displayRecordCount().includes(caseType)) {
            displayValue = `Record ${parseInt(activeItemIndex) + 1}`;
          } else {
            displayValue = getItemDisplayValue(
              activeItem,
              caseType ? caseType : "",
              ItemDisplayKeys,
            );
          }
        } else if (value === subItemId || value === selectedItemId) {
          // ---- VULNERABILITY ----
          const activeVulnerability = vulnerabilityList.find(
            (item: any) => item.vulnerability_id === value,
          );
          const activePartnerVulnerability = partnerVulnerabilityList.find(
            (item: any) => item.vulnerability_id === value,
          );

          const vulnerabilityIndex = activeVulnerability
            ? vulnerabilityList.findIndex(
              (item: any) => item.vulnerability_id === value,
            ) + 1
            : activePartnerVulnerability
              ? partnerVulnerabilityList.findIndex(
                (item: any) => item.vulnerability_id === value,
              ) + 1
              : 0;

          // ---- CONTACTS (client-action, notes, time-allocation) ----
          const contactSections: any = {
            "client-action": {
              list: clientContacts?.clientActions?.listItems,
              idKey: "appointment_id",
            },
            notes: {
              list: clientContacts?.notes?.listItems,
              idKey: "note_id",
            },
            "time-allocation": {
              list: clientContacts?.timeAllocations?.listItems,
              idKey: "timeallocation_id",
            },
          };

          const sectionKey = location.state?.section || "";
          const activeContactSection = contactSections[sectionKey];

          let contactIndex = 0;

          if (activeContactSection?.list?.length > 0) {
            const activeContactItem = activeContactSection.list.find(
              (item: any) => item[activeContactSection.idKey] === value,
            );
            if (activeContactItem) {
              contactIndex =
                activeContactSection.list.findIndex(
                  (item: any) => item[activeContactSection.idKey] === value,
                ) + 1;
            }
          }

          // ---- DISPLAY VALUE LOGIC ----
          if (
            location.state?.index &&
            location.state.index !== undefined &&
            displayRecordCount().includes(location.state?.section || "")
          ) {
            displayValue = `Record ${location.state.index}`;
          } else if (vulnerabilityIndex > 0) {
            displayValue = `Record ${vulnerabilityIndex}`;
          } else if (contactIndex > 0) {
            displayValue = `Record ${contactIndex}`;
          } else {
            displayValue = getItemDisplayValue(
              location.state?.selectedItem,
              sectionKey,
              ItemDisplayKeys,
            );
          }
        } else if (value === subSubItemId || value === distributionId) {
          displayValue = "Detail";
        } else if (value === itemId && matchedSection) {
          const list = recordMaps[matchedSection] || [];
          const recordIndex = list.findIndex((item: any) => item.id === itemId);

          displayValue =
            recordIndex >= 0 ? `Record ${recordIndex + 1}` : "Record";
        } else if (value === action) {
          displayValue = action
            ? action.charAt(0).toUpperCase() + action.slice(1)
            : "View";
        } else if (value === userId) {
          const recordIndex = userListItems.findIndex(
            (item: any) => item.user_id === userId,
          );
          displayValue =
            recordIndex >= 0 ? `Record ${recordIndex + 1}` : "Record";
        } else {
          displayValue = breadcrumbNameMap[value] || decodeURIComponent(value);
        }
        return (
          <React.Fragment key={to}>
            <BreadcrumbDivider />
            <BreadcrumbItem>
              {isLast ? (
                <BreadcrumbButton current>
                  {breadcrumbNameMap[displayValue] || displayValue}
                </BreadcrumbButton>
              ) : (
                <BreadcrumbButton
                  onClick={
                    displayValue?.startsWith("Record")
                      ? undefined
                      : () =>
                        displayValue?.toLowerCase().includes("clients")
                          ? handleNavigationLink("/")
                          : handleNavigationLink(to)
                  }
                >
                  {breadcrumbNameMap[displayValue] || displayValue}
                </BreadcrumbButton>
              )}
            </BreadcrumbItem>
          </React.Fragment>
        );
      })}
    </Breadcrumb>
  );
};

export default Breadcrumbs;
